/**
 * ApprovalsHistory — last-30-day decided approvals view.
 *
 * Sources both:
 *   1. Graph completed approvals (via useApprovalsList with status: 'completed')
 *   2. Local SessionEvents fallback for approvals predating the Graph rollout
 *      (kept under a collapsed accordion so legacy data doesn't disappear).
 *
 * Replaces the previous "read-only feed from SessionEvents" view that linked
 * users out to Microsoft Teams. Per Bill 2026-04-24: PCB does not punt the
 * approver to Teams — everything renders here.
 */

import { useMemo, useState } from 'react';
import { Button, Pill } from '@/components/primitives';
import { formatLocal, formatRelative } from '@/utils/time';
import { ApprovalCard } from './ApprovalCard';
import { ApprovalDecisionModal } from './ApprovalDecisionModal';
import { useApprovalsList } from './hooks/useApprovalsList';
import type { GraphApproval, ApprovalDecision } from './services/graphApprovalsClient';

export interface ApprovalsHistoryProps {
  windowDays?: number;
}

export function ApprovalsHistory({ windowDays = 30 }: ApprovalsHistoryProps): JSX.Element {
  const { approvals, loading, error, isLive, fallbackReason, refresh } = useApprovalsList({
    status: 'completed',
    top: 100,
  });
  const [windowSel, setWindowSel] = useState<number>(windowDays);
  const [detailFor, setDetailFor] = useState<GraphApproval | null>(null);

  const filtered = useMemo<ReadonlyArray<GraphApproval>>(() => {
    const cutoff = Date.now() - windowSel * 24 * 60 * 60 * 1000;
    return approvals.filter((a) => Date.parse(a.createdDateTime) >= cutoff);
  }, [approvals, windowSel]);

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-5)',
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>Window:</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            className={`f-chip ${d === windowSel ? 'is-on' : ''}`}
            onClick={() => setWindowSel(d)}
          >
            {d}d
          </button>
        ))}
        <Button variant="ghost" size="sm" onClick={refresh}>
          Refresh
        </Button>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-500)' }}>
          {isLive ? 'Live · Microsoft Graph' : 'Mock dataset (live integration pending)'}
        </span>
      </div>

      {fallbackReason && !isLive && (
        <div className="rap-source-banner is-warn" role="status">
          <span className="rap-source-banner__dot" aria-hidden />
          <span>{fallbackReason}</span>
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-5)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-600)' }}>Loading history…</div>
      ) : filtered.length === 0 ? (
        <div className="rap-empty" style={{ display: 'block' }}>
          <p className="rap-empty__title" style={{ textAlign: 'center' }}>
            Nothing decided in the last {windowSel} days
          </p>
          <p className="rap-empty__msg" style={{ textAlign: 'center' }}>
            Decisions made via Microsoft Teams Approvals will appear here as soon as the live
            integration is enabled.
          </p>
        </div>
      ) : (
        <table className="rap-history-table" aria-label="Decided approvals history">
          <thead>
            <tr>
              <th scope="col">Subject</th>
              <th scope="col">Requester</th>
              <th scope="col">Result</th>
              <th scope="col">Decided</th>
              <th scope="col">Comment</th>
              <th scope="col" style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const decided = a.stages.find((s) => s.reviewedDateTime);
              const variant =
                a.overallStatus === 'approved'
                  ? 'ok'
                  : a.overallStatus === 'rejected'
                    ? 'err'
                    : 'muted';
              const label =
                a.overallStatus === 'approved'
                  ? 'Approved'
                  : a.overallStatus === 'rejected'
                    ? 'Rejected'
                    : 'Closed';
              return (
                <tr key={a.id}>
                  <td>
                    <strong style={{ fontSize: 13, color: 'var(--ink-900)' }}>
                      {a.displayName}
                    </strong>
                    <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>
                      {a.customProperties['customer']
                        ? `Customer: ${a.customProperties['customer']}`
                        : a.sourceApp}
                    </div>
                  </td>
                  <td>{a.requesterDisplayName}</td>
                  <td>
                    <Pill variant={variant}>{label}</Pill>
                  </td>
                  <td>
                    {decided?.reviewedDateTime ? (
                      <span title={formatLocal(decided.reviewedDateTime)}>
                        {formatRelative(decided.reviewedDateTime)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ maxWidth: 280, fontSize: 12, color: 'var(--ink-700)' }}>
                    {decided?.justification || <em style={{ color: 'var(--ink-400)' }}>None</em>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Button variant="ghost" size="sm" onClick={() => setDetailFor(a)}>
                      View
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {detailFor && (
        <ApprovalDetailModal
          approval={detailFor}
          onClose={() => setDetailFor(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Read-only detail modal — also used from the pending grid via View detail.
// ---------------------------------------------------------------------------

import { ModalShell } from '@/components/primitives';

export function ApprovalDetailModal({
  approval,
  onClose,
}: {
  approval: GraphApproval;
  onClose: () => void;
}): JSX.Element {
  const variant =
    approval.overallStatus === 'approved'
      ? 'ok'
      : approval.overallStatus === 'rejected'
        ? 'err'
        : approval.overallStatus === 'pending'
          ? 'warn'
          : 'muted';
  const label =
    approval.overallStatus.charAt(0).toUpperCase() + approval.overallStatus.slice(1);

  return (
    <ModalShell
      open
      onClose={onClose}
      title={approval.displayName}
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: 'var(--sp-4) var(--sp-6)' }}>
        <span style={{ color: 'var(--ink-500)', fontSize: 12 }}>Status</span>
        <Pill variant={variant}>{label}</Pill>

        <span style={{ color: 'var(--ink-500)', fontSize: 12 }}>Requester</span>
        <span style={{ fontSize: 13 }}>{approval.requesterDisplayName}</span>

        <span style={{ color: 'var(--ink-500)', fontSize: 12 }}>Submitted</span>
        <span style={{ fontSize: 13 }} title={formatLocal(approval.createdDateTime)}>
          {formatRelative(approval.createdDateTime)}
        </span>

        {approval.dueDateTime && (
          <>
            <span style={{ color: 'var(--ink-500)', fontSize: 12 }}>Due</span>
            <span style={{ fontSize: 13 }} title={formatLocal(approval.dueDateTime)}>
              {formatRelative(approval.dueDateTime)}
            </span>
          </>
        )}

        <span style={{ color: 'var(--ink-500)', fontSize: 12 }}>Source</span>
        <span style={{ fontSize: 13 }}>{approval.sourceApp}</span>
      </div>

      {Object.keys(approval.customProperties).length > 0 && (
        <div className="rap-decision-summary">
          <h3 className="rap-decision-summary__title">Request details</h3>
          {Object.entries(approval.customProperties).map(([k, v]) => (
            <div key={k} className="rap-decision-summary__row">
              <span className="rap-decision-summary__key">{k}</span>
              <span>{v}</span>
            </div>
          ))}
        </div>
      )}

      {approval.requestDetail && (
        <div>
          <h4 style={{ fontSize: 12, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 var(--sp-3)' }}>
            Submitter notes
          </h4>
          <p style={{ fontSize: 13, color: 'var(--ink-800)', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' }}>
            {approval.requestDetail}
          </p>
        </div>
      )}

      <div>
        <h4 style={{ fontSize: 12, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 var(--sp-3)' }}>
          Stages
        </h4>
        <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {approval.stages.map((s) => (
            <li
              key={s.id}
              style={{
                padding: 'var(--sp-4) var(--sp-5)',
                background: 'var(--surface-card)',
                border: '1px solid var(--surface-border)',
                borderRadius: 'var(--r-sm)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-2)' }}>
                <strong style={{ fontSize: 13 }}>{s.displayName ?? 'Decision'}</strong>
                <Pill
                  variant={
                    s.status === 'completed'
                      ? s.reviewResult === 'approve'
                        ? 'ok'
                        : 'err'
                      : s.status === 'inProgress'
                        ? 'warn'
                        : 'muted'
                  }
                >
                  {s.status === 'completed'
                    ? s.reviewResult === 'approve'
                      ? 'Approved'
                      : 'Rejected'
                    : s.status === 'inProgress'
                      ? 'Awaiting decision'
                      : s.status}
                </Pill>
              </div>
              {s.justification && (
                <div style={{ fontSize: 12, color: 'var(--ink-700)', lineHeight: 1.4 }}>
                  {s.justification}
                </div>
              )}
              {s.reviewedDateTime && (
                <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 'var(--sp-2)' }}>
                  Decided {formatRelative(s.reviewedDateTime)}
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* Hidden but kept for future deep-link if a user wants the raw Teams card.
          Bill 2026-04-24: per-card "Open in Teams" link is allowed (it's a
          fallback/audit aid), but the main surface is here. */}
    </ModalShell>
  );
}
